import type { SalesOrderLineItemDto } from '../types/salesOrder.types';
import { totalLineWeightKg } from './lineWeight';

// 14ykpqtv3qx — the submit check's total-tonnage rule, mirrored so the screen and the server always agree about
// whether an order posts. Server: SalesOrderTonnageGate (CSC_CORE/Features/SalesOrders/Services) — change both
// together. The SO is weighed with DOP's kg per piece and the FO total is keyed outside DOP, so a full drawdown can
// land a fraction of a kilogram over (prod SO 227: 0.63 kg of 705 t). NetSuite accepts ANY over-draw, so the
// allowance is the most per-piece rounding can add — half a gram per bar — and never more.

/** Half a gram per bar (owner, 2026-10-14). Keep equal to SalesOrderTonnageGate.AllowanceKgPerPiece. */
export const ALLOWANCE_KG_PER_PIECE = 0.0005;

export interface TonnageCheck {
  requestedKg: number;
  availableKg: number;
  allowanceKg: number;
  /** How far past the FO balance the order reaches (0 when it fits) — positive on an allowed order too. */
  overBalanceKg: number;
  isRefused: boolean;
}

const round2 = (v: number) => Math.round((v + Number.EPSILON) * 100) / 100;

export function evaluateTonnage(requestedKg: number, availableKg: number, totalPieces: number): TonnageCheck {
  const requested = round2(requestedKg);
  const available = round2(availableKg);
  const allowance = round2(Math.max(totalPieces, 0) * ALLOWANCE_KG_PER_PIECE);
  return {
    requestedKg: requested,
    availableKg: available,
    allowanceKg: allowance,
    overBalanceKg: Math.max(round2(requested - available), 0),
    isRefused: requested > round2(available + allowance),
  };
}

/** The check over a draft's lines: pieces counted over the lines the server keeps (quantity > 0). */
export function evaluateLinesTonnage(
  lines: readonly Pick<SalesOrderLineItemDto, 'quantity' | 'weightPerPcKg' | 'weightKg'>[],
  availableKg: number,
): TonnageCheck {
  const kept = lines.filter((li) => (li.quantity ?? 0) > 0);
  const pieces = kept.reduce((sum, li) => sum + (li.quantity ?? 0), 0);
  return evaluateTonnage(totalLineWeightKg(kept), availableKg, pieces);
}
